import { useTranslation } from "react-i18next";
import type { MatchRequestResultItem, MatchResultCompany } from "../../config/api";
import { EMPTY_VALUE, formatCurrency, formatDate } from "../../i18n/format";
import { locationLabel } from "../../lib/regionOptions";
import { trustBadgeLabel, trustBadgeVariant } from "../../lib/trustLabels";
import CompanyAvatar from "../CompanyAvatar";
import DealOutcomeSummary from "../DealOutcome/DealOutcomeSummary";
import MatchExplanation from "../MatchExplanation/MatchExplanation";
import "./CompanyProfileModal.css";

interface CompanyProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: MatchRequestResultItem;
  onInitiateContact?: (matchResultId: number) => void;
  /** Set while chat can't open yet (WP1.6: supplier hasn't accepted). */
  contactBlockedReason?: string | null;
}

const CompanyProfileModal = ({
  isOpen,
  onClose,
  result,
  onInitiateContact,
  contactBlockedReason,
}: CompanyProfileModalProps) => {
  const { t } = useTranslation(["b2b", "common"]);
  if (!isOpen) return null;

  const company: MatchResultCompany = result.company;
  const location = locationLabel(company.country, company.region);
  const matched = result.matchedItem;

  return (
    <div className="company-profile-modal__overlay" onClick={onClose}>
      <div className="company-profile-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="company-profile-modal__header-bar">
          <div className="company-profile-modal__header-main">
            <CompanyAvatar src={company.companyAvatar} name={company.companyName} size="lg" />
            <h2 className="company-profile-modal__company-name">{company.companyName}</h2>
          </div>
          <button
            type="button"
            className="company-profile-modal__close"
            onClick={onClose}
            aria-label={t("common:actions.close")}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="company-profile-modal__body">
          <div className="company-profile-modal__score-row">
            <div className="company-profile-modal__score-box">
              <span className="company-profile-modal__score-label">{t("companyProfile.matchScore")}</span>
              <span className="company-profile-modal__score-value">{result.winWinMatchScore}%</span>
            </div>
            <div
              className={`company-profile-modal__trust-badge company-profile-modal__trust-badge--${trustBadgeVariant(company)}`}
            >
              <i className="fa-solid fa-shield-halved" />
              <span>{trustBadgeLabel(company)}</span>
            </div>
          </div>

          <section className="company-profile-modal__section">
            <h3 className="company-profile-modal__section-title">{t("companyProfile.overview")}</h3>
            <p className="company-profile-modal__description">{company.companyDescription || EMPTY_VALUE}</p>
            <div className="company-profile-modal__meta-grid">
              <div className="company-profile-modal__meta-item">
                <i className="fa-solid fa-building" />
                <span>{t("companyProfile.industry", { industry: company.companyIndustry || EMPTY_VALUE })}</span>
              </div>
              <div className="company-profile-modal__meta-item">
                <i className="fa-solid fa-location-dot" />
                <span>{t("companyProfile.location", { location: location || EMPTY_VALUE })}</span>
              </div>
            </div>
          </section>

          <section className="company-profile-modal__section">
            <h3 className="company-profile-modal__section-title">{t("companyProfile.matchedItem")}</h3>
            <div className="company-profile-modal__matched-box">
              <span className="company-profile-modal__matched-name">{matched?.name || EMPTY_VALUE}</span>
              {matched?.hsCode && (
                <span className="company-profile-modal__matched-hs">
                  {t("companyProfile.hsCode", { code: matched.hsCode })}
                </span>
              )}
            </div>
          </section>

          <section className="company-profile-modal__section">
            <h3 className="company-profile-modal__section-title">{t("companyProfile.ahpCriteria")}</h3>
            <div className="company-profile-modal__criteria-list">
              <div className="company-profile-modal__criterion">
                <span className="company-profile-modal__criterion-name">{t("companyProfile.industryMatch")}</span>
                <div className="company-profile-modal__criterion-bar-wrapper">
                  <div className="company-profile-modal__criterion-bar" style={{ width: `${result.industryMatch}%` }} />
                </div>
                <span className="company-profile-modal__criterion-value">{result.industryMatch}%</span>
              </div>
              <div className="company-profile-modal__criterion">
                <span className="company-profile-modal__criterion-name">{t("companyProfile.geographicLocation")}</span>
                <div className="company-profile-modal__criterion-bar-wrapper">
                  <div
                    className="company-profile-modal__criterion-bar"
                    style={{ width: `${result.geographicLocation}%` }}
                  />
                </div>
                <span className="company-profile-modal__criterion-value">{result.geographicLocation}%</span>
              </div>
            </div>
          </section>

          <section className="company-profile-modal__section">
            <MatchExplanation lines={result.explanation} confidence={result.matchConfidence} />
          </section>

          {result.outcome && (
            <section className="company-profile-modal__section">
              <DealOutcomeSummary outcome={result.outcome} />
            </section>
          )}

          {result.timeline && result.timeline.length > 0 && (
            <section className="company-profile-modal__section">
              <h2 className="company-profile-modal__section-title">{t("companyProfile.dealTimeline")}</h2>
              <ul className="company-profile-modal__timeline">
                {result.timeline.map((item) => (
                  <li
                    key={item.name}
                    className={`company-profile-modal__timeline-item company-profile-modal__timeline-item--${item.status}`}
                  >
                    <span className="company-profile-modal__timeline-icon">
                      {item.status === "done" ? (
                        <i className="fa-solid fa-circle-check" />
                      ) : item.status === "not_done" ? (
                        <i className="fa-solid fa-circle-xmark" />
                      ) : (
                        <i className="fa-regular fa-circle" />
                      )}
                    </span>
                    <span className="company-profile-modal__timeline-name">{item.name}</span>
                    {item.date ? (
                      <span className="company-profile-modal__timeline-date">{formatDate(item.date)}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {result.contract && (
            <section className="company-profile-modal__section">
              <h2 className="company-profile-modal__section-title">{t("companyProfile.contract")}</h2>
              <p className="company-profile-modal__contract-meta">
                <span className={`company-profile-modal__chip company-profile-modal__chip--${result.contract.status}`}>
                  {result.contract.status}
                </span>
                <span> · {result.contract.contractType}</span>
                {result.contract.contractValue ? <span> · {formatCurrency(result.contract.contractValue)}</span> : null}
              </p>
              {result.contract.documents && result.contract.documents.length > 0 && (
                <ul className="company-profile-modal__documents">
                  {result.contract.documents.map((d, i) => (
                    <li key={i}>
                      <a href={d} target="_blank" rel="noopener noreferrer">
                        <i className="fa-regular fa-file-pdf" /> {t("companyProfile.document", { index: i + 1 })}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <div className="company-profile-modal__actions">
            <button
              type="button"
              className="company-profile-modal__btn company-profile-modal__btn--secondary"
              onClick={onClose}
            >
              {t("common:actions.close")}
            </button>
            <button
              type="button"
              className="company-profile-modal__btn company-profile-modal__btn--primary"
              disabled={Boolean(contactBlockedReason)}
              title={contactBlockedReason ?? undefined}
              onClick={() => {
                onInitiateContact?.(result.id);
                onClose();
              }}
            >
              <i className="fa-solid fa-paper-plane" />
              {t("companyProfile.initiateContact")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileModal;
